import React from "react";
import { Button, Card, Modal, Space, Tag, Typography } from "antd";
import { LockOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { useUpdateSchoolStatusMutation } from "../../../../store/api/schoolsApi";
import { useNotification } from "../../../../hooks/useNotification";

const { Text, Title } = Typography;

interface SchoolStatusCardProps {
  schoolId: number;
  isActive: boolean;
  /** Reports the persisted flag so the parent can re-render without a refetch. */
  onStatusChange: (isActive: boolean) => void;
}

/**
 * School access switch (SCRUM-748), rendered at the bottom of the Escuela tab.
 *
 * Deactivating is a BLOCK, not a deletion: the confirmation copy is frozen by
 * the ticket precisely because an admin must know nothing is lost. The button
 * is the only entry point — the flag is never part of the school form payload,
 * so saving the form can never flip access by accident.
 */
const SchoolStatusCard: React.FC<SchoolStatusCardProps> = ({
  schoolId,
  isActive,
  onStatusChange,
}) => {
  const [updateSchoolStatus, { isLoading }] = useUpdateSchoolStatusMutation();
  const { notify } = useNotification();
  // Context-based Modal API: AntD v5 static Modal.confirm does not render
  // under React 19.
  const [modal, modalContextHolder] = Modal.useModal();

  const applyStatus = async (nextIsActive: boolean) => {
    try {
      const updated = await updateSchoolStatus({
        id: schoolId,
        is_active: nextIsActive,
      }).unwrap();

      onStatusChange(updated.is_active);
      notify(
        "success",
        "Éxito",
        nextIsActive
          ? "La escuela fue reactivada. Sus usuarios recuperaron el acceso."
          : "La escuela fue desactivada. Sus usuarios ya no pueden acceder."
      );
    } catch (error: any) {
      const backendMessage = error?.data?.message;
      notify(
        "error",
        "Error",
        backendMessage
          ? `No se pudo cambiar el estado de la escuela.\n${backendMessage}`
          : "No se pudo cambiar el estado de la escuela. Reintentá en unos segundos."
      );
    }
  };

  const confirmDeactivate = () => {
    modal.confirm({
      title: "¿Desactivar esta escuela?",
      content:
        "Todos los usuarios asociados dejarán de tener acceso a esta escuela y no podrán utilizar sus programas, clases ni contenido. La información y el historial de la escuela se conservarán.",
      okText: "Desactivar escuela",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: () => applyStatus(false),
    });
  };

  const confirmReactivate = () => {
    modal.confirm({
      title: "¿Reactivar esta escuela?",
      content:
        "La escuela y sus usuarios recuperarán el acceso con toda la información anterior intacta.",
      okText: "Reactivar escuela",
      cancelText: "Cancelar",
      onOk: () => applyStatus(true),
    });
  };

  return (
    <Card style={{ marginTop: 24 }}>
      {modalContextHolder}
      <div className="school-status-card">
        <Space align="start" size={16}>
          {isActive ? (
            <SafetyCertificateOutlined
              style={{ fontSize: 24 }}
              aria-hidden="true"
            />
          ) : (
            <LockOutlined style={{ fontSize: 24 }} aria-hidden="true" />
          )}
          <div>
            <Title level={5} style={{ marginBottom: 8 }}>
              Estado de la escuela
            </Title>
            <Tag color={isActive ? "green" : "red"}>
              {isActive ? "Activa" : "Inactiva"}
            </Tag>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                {isActive
                  ? "Todos los usuarios pueden acceder y utilizar esta escuela."
                  : "Nadie puede acceder a esta escuela ni utilizar sus programas, clases o contenido."}
              </Text>
            </div>
          </div>
        </Space>

        <div className="school-status-card__action">
          <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
            {isActive
              ? "Al desactivar, nadie podrá acceder a esta escuela ni utilizar sus programas, clases o contenido. La información y el historial se conservarán."
              : "Al reactivar, sus usuarios recuperan el acceso con toda la información anterior intacta."}
          </Text>
          {isActive ? (
            <Button
              danger
              icon={<LockOutlined />}
              loading={isLoading}
              onClick={confirmDeactivate}
            >
              Desactivar escuela
            </Button>
          ) : (
            <Button type="primary" loading={isLoading} onClick={confirmReactivate}>
              Reactivar escuela
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

export default SchoolStatusCard;
